import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router';
import { Layout } from '@/components/Layout';
import { RouteError } from '@/components/RouteError';
import Home from '@/pages/Home';
import NotFound from '@/pages/NotFound';

function OldJobLink() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/guide/jobs${slug ? `/${slug}` : ''}${search}${hash}`} replace />;
}

// Pages other than the landing page load on demand, so the first visit only
// downloads what the landing page needs.
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({ Component: (await load()).default });
const named =
  <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  async () => ({ Component: (await load())[name] });

export const router = createBrowserRouter([
  {
    element: <Layout />,
    // Last resort: the layout itself failed.
    errorElement: <RouteError full />,
    children: [
      {
        // A page that fails shows the error INSIDE the layout (header and footer stay).
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Home /> },
          { path: 'download', lazy: page(() => import('@/pages/Download')) },
          { path: 'pre-register', lazy: page(() => import('@/pages/PreRegister')) },
          { path: 'news', lazy: named(() => import('@/pages/News'), 'NewsList') },
          { path: 'news/:slug', lazy: named(() => import('@/pages/News'), 'NewsArticle') },
          { path: 'rankings', lazy: page(() => import('@/pages/Rankings')) },
          { path: 'online', lazy: page(() => import('@/pages/Online')) },
          { path: 'legal/:page', lazy: page(() => import('@/pages/Legal')) },
          { path: 'woe', lazy: page(() => import('@/pages/world/Woe')) },
          { path: 'guilds', lazy: named(() => import('@/pages/world/Guilds'), 'GuildList') },
          { path: 'guilds/:id', lazy: named(() => import('@/pages/world/Guilds'), 'GuildPage') },
          { path: 'mvp', lazy: page(() => import('@/pages/world/Mvp')) },
          { path: 'hall-of-fame', lazy: page(() => import('@/pages/world/HallOfFame')) },
          {
            path: 'guide',
            lazy: page(() => import('@/pages/guide/GuideLayout')),
            children: [
              { index: true, lazy: page(() => import('@/pages/guide/GuideHome')) },
              { path: 'jobs', lazy: named(() => import('@/pages/jobs/Jobs'), 'JobsIndex') },
              { path: 'jobs/:slug', lazy: named(() => import('@/pages/jobs/Jobs'), 'JobPage') },
              { path: ':slug', lazy: page(() => import('@/pages/guide/GuideArticle')) },
            ],
          },
          // The job guide lived here before the player guide; old links still land.
          { path: 'jobs', element: <OldJobLink /> },
          { path: 'jobs/:slug', element: <OldJobLink /> },
          { path: 'vote', lazy: page(() => import('@/pages/Vote')) },
          { path: 'market', lazy: page(() => import('@/pages/world/Market')) },
          { path: 'database', lazy: named(() => import('@/pages/database/Database'), 'ItemSearch') },
          { path: 'database/items/:id', lazy: named(() => import('@/pages/database/Database'), 'ItemPage') },
          { path: 'database/monsters', lazy: named(() => import('@/pages/database/Database'), 'MobSearch') },
          { path: 'database/mobs/:id', lazy: named(() => import('@/pages/database/Database'), 'MobPage') },
          { path: 'database/maps/:map', lazy: named(() => import('@/pages/database/Database'), 'MapPage') },
          { path: 'database/cash-shop', lazy: named(() => import('@/pages/database/Database'), 'CashShop') },

          { path: 'login', lazy: page(() => import('@/pages/auth/Login')) },
          { path: 'register', lazy: page(() => import('@/pages/auth/Register')) },
          { path: 'forgot-password', lazy: named(() => import('@/pages/auth/Recovery'), 'Forgot') },
          { path: 'reset-password', lazy: named(() => import('@/pages/auth/Recovery'), 'Reset') },
          { path: 'account/verify', lazy: named(() => import('@/pages/auth/Recovery'), 'Verify') },
          {
            path: 'account',
            lazy: page(() => import('@/pages/account/AccountLayout')),
            children: [
              { index: true, lazy: page(() => import('@/pages/account/Overview')) },
              { path: 'history', lazy: page(() => import('@/pages/account/History')) },
              { path: 'referrals', lazy: page(() => import('@/pages/account/Referrals')) },
              { path: 'redeem', lazy: page(() => import('@/pages/account/Redeem')) },
              { path: 'security', lazy: page(() => import('@/pages/account/Security')) },
            ],
          },

          { path: 'donate', lazy: page(() => import('@/pages/donate/Donate')) },
          { path: 'donate/order/:id', lazy: page(() => import('@/pages/donate/Order')) },
          { path: 'resellers', lazy: page(() => import('@/pages/partners/Resellers')) },
          { path: 'partners', lazy: named(() => import('@/pages/partners/Partners'), 'Partners') },
          { path: 'partners/apply', lazy: named(() => import('@/pages/partners/Partners'), 'PartnerApply') },
          {
            path: 'partner',
            lazy: page(() => import('@/pages/partner/PartnerLayout')),
            children: [
              { index: true, lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerOverview') },
              { path: 'requests', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerRequests') },
              { path: 'send', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerSend') },
              { path: 'topup', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerTopup') },
              { path: 'history', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerHistory') },
              { path: 'creator', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerCreator') },
              { path: 'profile', lazy: named(() => import('@/pages/partner/PartnerPages'), 'PartnerProfilePage') },
            ],
          },
          {
            path: 'admin',
            lazy: page(() => import('@/pages/admin/AdminLayout')),
            children: [
              { index: true, lazy: page(() => import('@/pages/admin/AdminOverview')) },
              { path: 'analytics', lazy: page(() => import('@/pages/admin/analytics/AdminAnalytics')) },
              { path: 'analytics/:tab', lazy: page(() => import('@/pages/admin/analytics/AdminAnalytics')) },
              { path: 'payments', lazy: page(() => import('@/pages/admin/AdminPayments')) },
              { path: 'players', lazy: page(() => import('@/pages/admin/AdminAccounts')) },
              { path: 'accounts', lazy: page(() => import('@/pages/admin/AdminAccounts')) },
              { path: 'online', lazy: page(() => import('@/pages/admin/AdminOnline')) },
              { path: 'lounge', lazy: page(() => import('@/pages/admin/AdminLounge')) },
              { path: 'server', lazy: page(() => import('@/pages/admin/AdminServer')) },
              { path: 'devices', lazy: page(() => import('@/pages/admin/AdminDevices')) },
              { path: 'drops', lazy: page(() => import('@/pages/admin/AdminDrops')) },
              { path: 'game', lazy: page(() => import('@/pages/admin/AdminGame')) },
              { path: 'schedule', lazy: page(() => import('@/pages/admin/AdminSchedule')) },
              { path: 'codes', lazy: named(() => import('@/pages/admin/AdminCodes'), 'AdminCodes') },
              { path: 'codes/:id', lazy: named(() => import('@/pages/admin/AdminCodes'), 'AdminCodeBatch') },
              { path: 'prereg', lazy: page(() => import('@/pages/admin/AdminPrereg')) },
              { path: 'partners', lazy: page(() => import('@/pages/admin/AdminPartners')) },
              { path: 'news', lazy: page(() => import('@/pages/admin/AdminNews')) },
              { path: 'votes', lazy: page(() => import('@/pages/admin/AdminVotes')) },
              { path: 'settings', lazy: page(() => import('@/pages/admin/AdminSettings')) },
              { path: 'audit', lazy: page(() => import('@/pages/admin/AdminAudit')) },
            ],
          },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
